import { IField, getJsonKeyFromField } from '@eventr/common'
import { ValidatorProvider } from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import React, { useMemo } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { useBoundStore } from '../../state/bound.store'
import { HotelAvailPage } from '../hotelBooking/hotelAvailPage'
import { HotelBookingPage } from '../hotelBooking/hotelBookingPage'
import { HotelDetailsPage } from '../hotelBooking/hotelDetailsPage'
import { HotelReturnPage } from '../hotelBooking/hotelReturnPage'
import { ProviderManagePage } from '../hotelBooking/providerManagePage'
import { ActivitiesListPage } from './activities/activitiesListPage'
import { ActivityEditPage } from './activities/activityEditItem'
import { CreateNewItineraryDynamic } from './CreateNewItinerary'
import { EditItem } from './EditItem'
import { EditItinerarySectionSelection } from './EditItinerarySectionSelection'
import { EditMembers } from './editMembers/EditMembers'
import { EditMembersProvider } from './editMembers/editMembersProvider'
import { EditSection } from './EditSection'
import { EditSectionProvider } from './editSectionProvider'
import { PublicItinPage } from './subPages/PublicItinPage'
import { SubPageFinder } from './subPages/subPageFinder'
import { TodoEditPage } from './todo/todoEditItem'

export const EditItineraryRoutes = () => {
    const location = useLocation()
    const [key1, key2] = useMemo(() => {
        if (location.pathname.includes('/Section/')) {
            return location.pathname.split('/Section/')
        } else if (location.pathname.includes('/activities/')) {
            return location.pathname.split('/activities/')
        } else {
            return [location.pathname, '']
        }
    }, [location])

    return (
        <Stack height="100%">
            {/* <AnimatePresence mode="sync"> */}
            <Routes location={location} key={key1}>
                <Route
                    key={'Selection'}
                    index
                    element={<EditItinerarySectionSelection />}
                />

                <Route
                    key={'CreateItinerary'}
                    path={`/newItin/:sectionID/:isNew`}
                    element={
                        <ValidatorProviderRoute>
                            <EditSectionProvider>
                                <CreateNewItineraryDynamic />
                            </EditSectionProvider>
                        </ValidatorProviderRoute>
                    }
                />
                <Route
                    key={'PublicItineraries'}
                    path={`/publicItins`}
                    element={<PublicItinPage />}
                />
                <Route
                    key={'EditSectionParent'}
                    path={`/Section/:sectionID/*`}
                    element={
                        <ValidatorProviderRoute>
                            <EditSectionProvider>
                                <Routes location={location} key={key2}>
                                    <Route
                                        key={'editSection'}
                                        index
                                        element={<EditSection />}
                                    />

                                    <Route
                                        key={'editItem'}
                                        path={`:idItem`}
                                        element={<EditItem />}
                                    />
                                    <Route
                                        key={'editSubItem'}
                                        path={`:idItem/:idSubPage/:idSubItem?`}
                                        element={<SubPageFinder />}
                                    />
                                </Routes>
                            </EditSectionProvider>
                        </ValidatorProviderRoute>
                    }
                />
                <Route
                    key={'activities'}
                    path={`/activities/*`}
                    element={
                        <Routes key={'activities'}>
                            <Route
                                key={'editTodoItem'}
                                path={`todos/:idItem`}
                                element={<TodoEditPage />}
                            />
                            <Route
                                key={'editActSection'}
                                path={`general`}
                                element={<ActivitiesListPage />}
                            />
                            <Route
                                key={'editActItem'}
                                path={`general/:idItem/*`}
                                element={<ActivityEditPage />}
                            ></Route>
                        </Routes>
                    }
                />

                <Route
                    key={'membersEditGeneric'}
                    path={`/members`}
                    element={
                        <EditMembersProvider>
                            <EditMembers />
                        </EditMembersProvider>
                    }
                />

                <Route key={'HotelBooking'} path={`/hotelbooking/*`}>
                    <Route
                        key={'hotelbooking_search'}
                        path={`search`}
                        element={<HotelBookingPage />}
                    />

                    <Route
                        key={'hotelbooking_details'}
                        path={`details`}
                        element={<HotelDetailsPage />}
                    />
                    <Route
                        key={'hotelbooking_avail'}
                        path={`avail`}
                        element={<HotelAvailPage />}
                    />
                    <Route
                        key={'hotelbooking_return'}
                        path={`return`}
                        element={<HotelReturnPage />}
                    />
                    <Route
                        key={'manageBooking'}
                        path={`manage/:idItem`}
                        element={<ProviderManagePage />}
                    />
                </Route>
            </Routes>
            {/* </AnimatePresence> */}
        </Stack>
    )
}

const ValidatorProviderRoute = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const { fields } = useBoundStore(
        useShallow((s) => {
            return {
                fields: s.fields,
            }
        })
    )

    const getFieldByIdFromFields = (fields: IField[], id: number) => {
        return fields?.find(
            (field) =>
                Number.parseInt(field.id?.toString()) ===
                Number.parseInt(id?.toString())
        )
    }

    const getFieldById = (id: number) => {
        return getFieldByIdFromFields(fields, id)
    }

    const buildPathToField = (fieldId: number) => {
        const path: string[] = []
        const field = getFieldById(fieldId)
        if (!field) return path
        const fieldJson = getJsonKeyFromField(field)
        path.push(fieldJson)

        let parent: number | undefined = field?.parent
        while (parent) {
            const json = getJsonKeyFromField(getFieldById(parent)!)

            path.push(json)
            parent = getFieldById(parent)?.parent
        }
        return path.reverse()
    }
    return (
        <ValidatorProvider buildPathToField={buildPathToField}>
            {children}
        </ValidatorProvider>
    )
}
